import { HERO_MOMENT_XML } from '../../../shared/heroMomentArt.js'
import { SECTION_CARD_XML } from '../../../shared/onboardingSectionArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import Hero from '../components/ds/Hero'
import StoreBadges from '../components/ds/StoreBadges'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import FaqAccordion from '../components/ds/FaqAccordion'
import Proof from '../components/ds/Proof'
import NotAnAgency from '../components/ds/NotAnAgency'
import PhoneFrame, { AppScreen } from '../components/ds/PhoneFrame'
import ProductTour from '../components/ds/ProductTour'
import { HERO_COVER } from '../content/heroMedia'
import { WORKPLACE_QUOTES } from '../content/quotes'
import { getCoverJsonLd } from '../lib/landingJsonLd'
import { dashboardUrl } from '../lib/dashboardUrl'

// /business — the Get cover trunk home.
//
// The reader is a workplace owner or manager with a hole in next week’s rota who
// is about to ring an agency. They want to know it will be filled, by somebody
// checked, at a price they can see.
//
// ⛔ Vocabulary (CLAUDE.md): never hire, staff, employ, temp, agency markup or
// middleman. They are self employed professionals ACCEPTING work. What a
// workplace gets is COVER.
//
// ⚠️ The hero here is a STILL, not video (founder 2026-10-02, Monzo’s own split).

const OUTCOMES = [
  { art: HERO_MOMENT_XML.shiftPosted, title: 'Smarter spend, automatically',
    body: "No agency margin, and no overpaying to get a gap filled. Smart pricing reads the market for you and secures great cover at the right rate, so your budget goes further without you lifting a finger." },
  { art: HERO_MOMENT_XML.offerSent, title: 'Peace of mind, every morning',
    body: "Know exactly who is walking through your door before they arrive. Verified credentials, a proven track record and a live ETA, so the day starts calm." },
  { art: HERO_MOMENT_XML.toppedUp, title: 'The right specialist, every time',
    body: "Implant nursing, sedation, paediatrics. Whatever the list calls for, your cover arrives with the skills to match, and the session runs as planned." },
]

const CHECKED = [
  'Professional registration verified, then watched, so nothing lapses between shifts',
  'Right to work checked to the standard you are obliged to hold',
  'Identity confirmed against photo ID, never a photocopy',
  'Every document the role needs, in date on the day they arrive',
]

const FAQS = [
  { q: 'How quickly can we get a shift covered?',
    a: 'Your shift reaches checked professionals the moment you post it, and you see interest as it arrives instead of waiting on a callback. How fast it fills depends on the role, the day and the rate, and if a shift is slow to move we tell you what would help.' },
  { q: 'Are they employed by us?',
    a: 'No. They are self employed professionals choosing to accept your shift. There is no employee to take on and no payroll to run.' },
  { q: 'What if nobody turns up?',
    a: 'Attendance is tracked on the day, so we know about a no show before you have to chase it, and we work the problem with you straight away.' },
  { q: 'Can we book the same professional again?',
    a: 'Yes. Keep a list of the professionals who fit your team and offer them shifts directly, so your best cover is always one step away.' },
  { q: 'How does smart pricing work?',
    a: 'You set the rate you would like to pay and the most you are willing to go to. Smart matching starts at your rate and only raises the offer when the shift needs it, as interest and the time left before the start tell it to. It never goes past your ceiling, and it never surprises you after the fact.' },
  { q: 'Can we ask for a particular specialism?',
    a: 'Yes. Pick the skills the session needs, from implant nursing to sedation and paediatric experience, and choose whether each is strictly required or a nice to have. Strict requirements are only offered to professionals who hold them.' },
  { q: 'What do we pay, and when?',
    a: 'You see the full cost before you post the shift. It settles once the shift is done, on one clear bill.' },
  { q: 'What if the hours were not what we agreed?',
    a: 'You have 24 hours after the shift to query the hours. If the two accounts do not match, we look at what actually happened, so neither side is taken on trust.' },
]

export default function GetCover() {
  return (
    <div className="bg-canvas">
      <Seo
        title="Dental locum cover, without the agency"
        description="Dental locum cover from checked, GDC registered professionals. Smart pricing inside your budget, live arrival times and one clear bill. No agency margin."
        path="/business"
        jsonLd={getCoverJsonLd(FAQS)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <Hero
        trust={<StoreBadges audience="cover" source="hero" tone="light" />}
        media={HERO_COVER}
        title={<>Every gap covered.<br />Every professional checked.</>}
        lead="Dental locum cover at the right price. Set your budget and smart matching finds a checked, GDC registered professional for the rate the shift really needs. No agency margin, no invoices, no ringing round."
      >
        <Button href={dashboardUrl('/register')} variant="light">Post a shift</Button>
        <Button to="/business/how-it-works" variant="quietLight">
          See how it works
        </Button>
      </Hero>

      <Section ground="canvas">
        <SectionHead eyebrow="Why workplaces switch" title={<>Better cover. Smarter spend. Total control.</>} />
        <div className="mt-12 grid gap-10 md:grid-cols-3">
          {OUTCOMES.map((o) => (
            <div key={o.title}>
              <Art xml={o.art} className="h-[120px] w-[160px]" />
              <h3 className="mt-4 text-[21px] font-semibold">{o.title}</h3>
              <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{o.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <NotAnAgency ground="surface" />

      <ProductTour
        ground="navy"
        eyebrow="The Hub"
        title="From gap to covered, without picking up the phone."
        lead="The Hub puts your whole rota in your pocket. Cover any shift, follow the day as it happens, and close it out with total confidence."
        steps={[
          { screen: <AppScreen id="hub-post-shift" />, title: 'Filled at the right rate',
            body: "Smart pricing finds the rate that gets your shift covered, with not a penny of agency margin on top." },
          { screen: <AppScreen id="hub-today" />, title: 'Live on the day',
            body: "Live ETAs, arrivals as they happen and a direct line to everyone booked. Nothing left to chance." },
          { screen: <AppScreen id="hub-timesheet" />, title: 'Hours you can trust',
            body: "Times come straight from the shift, so the timesheet is right first time. You have 24 hours to query anything, and every query is decided on what actually happened." },
        ]}
      />

      <Section ground="surface">
        <div className="flex flex-col items-center gap-14 md:flex-row md:justify-between md:gap-20">
          <div className="max-w-[460px]">
            <SectionHead eyebrow="And then it’s settled" title={<>One bill. Nothing to reconcile.</>} />
            <p className="mt-5 text-[19px] leading-relaxed text-ink-muted">
              No invoices to chase, no payroll to run, nothing to reconcile. Every shift is digital,
              trackable and on one clear bill. And professionals are paid fast, so your shifts are
              easy to say yes to.
            </p>
            <Button to="/business/pricing" className="mt-8">See what it costs</Button>
          </div>
          <PhoneFrame><AppScreen id="hub-paid" /></PhoneFrame>
        </div>
      </Section>

      <Section ground="canvas">
        <div className="md:flex md:items-start md:gap-20">
          <div className="md:w-[420px] md:shrink-0">
            <Art xml={SECTION_CARD_XML.business} className="h-[72px] w-[72px]" />
            <H2 className="mt-4">
              Checked before they can accept, not after they arrive.
            </H2>
            <Button to="/business/compliance" variant="outline" size="md" className="mt-7">
              What we check
            </Button>
          </div>
          <ul className="mt-10 flex-1 space-y-4 md:mt-2">
            {CHECKED.map((c) => (
              <li key={c} className="flex gap-4 border-b border-hairline pb-4 text-[18px] text-ink">
                <span aria-hidden="true" className="mt-1.5 shrink-0 text-action">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12l6 6L20 6" />
                  </svg>
                </span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section ground="surface">
        <div className="md:flex md:items-center md:gap-20">
          <div className="flex-1">
            <SectionHead eyebrow="What it costs" title={<>Smart pricing. No agency margin.</>} />
            <p className="mt-5 max-w-[560px] text-[19px] leading-relaxed text-ink-muted">
              The full cost is clear before you commit, and it always stays within your budget.
              No agency in the middle and no margin on top. Just great cover at a fair price.
            </p>
            <Button to="/business/pricing" className="mt-8">See what it costs</Button>
          </div>
          <Art xml={HERO_MOMENT_XML.ratesMatched} className="mt-10 h-[180px] w-[240px] shrink-0 md:mt-0" />
        </div>
      </Section>

      {/* Renders nothing until src/content/quotes.js has consented quotes. */}
      <Proof quotes={WORKPLACE_QUOTES} title="Workplaces already doing this." />

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand
        ground="navy"
        size="xl"
        title="Your next gap, covered at the right price."
        lead="Set up once. Cover every shift after that with checked professionals and one simple bill."
      >
        <Button href={dashboardUrl('/register')} variant="light">Get started</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
